import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Card, CardContent, Typography, Grid, LinearProgress, ToggleButtonGroup,
  ToggleButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Tabs, Tab, useTheme,
} from '@mui/material';
import {
  Assessment as ReportsIcon, Groups as CrossExecIcon, FolderOpen as ProjectsIcon,
  Speed as CapacityIcon, TrendingUp as DriversIcon, FileDownload as FileDownloadIcon,
  PieChartOutline as TimeAllocIcon, History as ActivityIcon,
  CheckCircleOutline, AddCircleOutline, EventNote, ShowChart, Edit as EditIcon,
  FolderSpecial, SwapHoriz as DelegationIcon,
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { workItemsApi, driversApi, reportingApi, projectsApi, exportApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type { WorkItem, AnnualStrategicDriver, WorkItemType, Priority, Project, StructuredKpi, TimeAllocationReport, ActivityFeedItem, DelegationReport } from '../types/models';
import HeadlineInsight from '../components/intelligence/HeadlineInsight';
import {
  deriveCapacityInsight,
  deriveDelegationInsight,
  deriveExecutiveInsight,
  deriveTimeAllocationInsight,
} from '../utils/intelligence';

const TYPE_COLORS: Record<string, string> = {
  Task: '#6C9BFF',
  Decision: '#FF8A65',
  Briefing: '#69F0AE',
  Review: '#FFB74D',
  FollowUp: '#CE93D8',
  Draft: '#4DD0E1',
  Reading: '#AED581',
};

type DateRange = 'week' | 'month' | 'lastMonth';

function useChartColors() {
  const theme = useTheme();
  return useMemo(() => ({
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    bgPaper: theme.palette.background.paper,
    divider: theme.palette.divider,
    tooltipBg: theme.palette.background.paper,
    gridLine: theme.palette.divider,
  }), [theme]);
}

function usePriorityColors() {
  const C = useChartColors();
  return useMemo(() => ({
    Critical: C.error,
    High: C.secondary,
    Medium: C.primary,
    Low: C.success,
  }), [C]);
}

function CustomTooltip({ active, payload, label }: any) {
  const C = useChartColors();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      backgroundColor: C.tooltipBg,
      border: '1px solid',
      borderColor: C.divider,
      borderRadius: 2,
      color: C.textPrimary,
      fontSize: 13,
      p: '8px 12px',
    }}>
      {label && <Typography variant="caption" sx={{ color: C.textSecondary, display: 'block', mb: 0.5 }}>{label}</Typography>}
      {payload.map((entry: any, i: number) => (
        <Typography key={i} variant="body2" sx={{ color: entry.color || C.textPrimary }}>
          {entry.name}: {entry.value}{typeof entry.value === 'number' && entry.name?.includes('Min') ? 'm' : ''}
        </Typography>
      ))}
    </Box>
  );
}

function getDateRange(range: DateRange): { start: Date; end: Date } {
  const now = new Date();
  if (range === 'week') {
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday start
    const start = new Date(now);
    start.setDate(now.getDate() - diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (range === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }
  // lastMonth
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { start, end };
}

function makeRenderCustomLabel(textColor: string) {
  return ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill={textColor} textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
}

export default function ReportsPage() {
  const { user } = useAuth();
  const C = useChartColors();
  const PRIORITY_COLORS = usePriorityColors();
  const renderCustomLabel = useMemo(() => makeRenderCustomLabel(C.textPrimary), [C.textPrimary]);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>('month');
  const [activeTab, setActiveTab] = useState(0);
  const [capacityForecast, setCapacityForecast] = useState<any>(null);
  const canSeeCrossExec = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.role === 'ExecutiveAssistant';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [itemsData, driversData] = await Promise.all([
          workItemsApi.getAll(),
          driversApi.getActive(),
        ]);
        setItems(itemsData);
        setDrivers(driversData);
      } catch (err) {
        console.error('Failed to load report data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // Load capacity forecast separately (non-blocking)
    reportingApi.getCapacityForecast(4).then(setCapacityForecast).catch(() => {});
  }, []);

  // Filter items by date range
  const filteredItems = useMemo(() => {
    const { start, end } = getDateRange(range);
    return items.filter(item => {
      const d = new Date(item.targetDate);
      return d >= start && d <= end;
    });
  }, [items, range]);

  // ---- Computed metrics ----

  // Time Allocation
  const timeAllocation = useMemo(() => {
    const focusMinutes = filteredItems
      .filter(i => i.type === 'Task' || i.type === 'Draft' || i.type === 'Reading')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const meetingMinutes = filteredItems
      .filter(i => i.type === 'Briefing' || i.type === 'Review')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const plannedMinutes = filteredItems
      .filter(i => i.status === 'Planned' || i.status === 'InProgress')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const overflowMinutes = filteredItems
      .filter(i => i.status === 'Deferred' || i.isCarryForward)
      .reduce((s, i) => s + i.estimatedMinutes, 0);

    return [
      { name: 'Focus Time', value: focusMinutes, color: C.primary },
      { name: 'Meetings', value: meetingMinutes, color: C.secondary },
      { name: 'Planned Work', value: plannedMinutes, color: C.success },
      { name: 'Overflow', value: overflowMinutes, color: C.error },
    ].filter(d => d.value > 0);
  }, [filteredItems, C]);

  // Work Item Breakdown by Type
  const typeBreakdown = useMemo(() => {
    const allTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
    return allTypes.map(type => {
      const ofType = filteredItems.filter(i => i.type === type);
      return {
        name: type,
        count: ofType.length,
        minutes: ofType.reduce((s, i) => s + i.estimatedMinutes, 0),
        color: TYPE_COLORS[type],
      };
    }).filter(d => d.count > 0);
  }, [filteredItems]);

  // Priority Distribution
  const priorityDistribution = useMemo(() => {
    const allPriorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
    return allPriorities.map(p => ({
      name: p,
      value: filteredItems.filter(i => i.priority === p).length,
      color: PRIORITY_COLORS[p],
    })).filter(d => d.value > 0);
  }, [filteredItems, PRIORITY_COLORS]);

  // Time Hogs — top 10 by estimated minutes
  const timeHogs = useMemo(() => {
    return [...filteredItems]
      .sort((a, b) => b.estimatedMinutes - a.estimatedMinutes)
      .slice(0, 10);
  }, [filteredItems]);

  // Strategic Alignment
  const strategicAlignment = useMemo(() => {
    const linked = filteredItems.filter(i => i.strategicDriverId).length;
    const unlinked = filteredItems.length - linked;
    return [
      { name: 'Linked to Strategy', value: linked, color: C.primary },
      { name: 'Unlinked', value: unlinked, color: C.textSecondary },
    ].filter(d => d.value > 0);
  }, [filteredItems, C]);

  // Carry-Forward Frequency
  const carryForwardData = useMemo(() => {
    const cfItems = filteredItems.filter(i => i.isCarryForward);
    // Group by title
    const counts: Record<string, { title: string; count: number; minutes: number }> = {};
    cfItems.forEach(item => {
      const key = item.title;
      if (!counts[key]) counts[key] = { title: key, count: 0, minutes: item.estimatedMinutes };
      counts[key].count += 1;
    });
    return Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredItems]);

  if (!user) return null;

  const cardSx = {
    height: '100%',
    bgcolor: 'background.paper',
  };

  const sectionTitle = (text: string) => (
    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>{text}</Typography>
  );

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (activeTab === 0) {
        const r = await exportApi.downloadWorkItemsCsv();
        triggerDownload(r.data, `work-items-${today}.csv`);
      } else if (activeTab === 2) {
        const r = await exportApi.downloadKpiHistoryCsv();
        triggerDownload(r.data, `kpi-history-${today}.csv`);
      } else if (activeTab === 3) {
        const r = await exportApi.downloadProjectsCsv();
        triggerDownload(r.data, `projects-${today}.csv`);
      }
    } catch {
      alert('Failed to export data.');
    }
  };

  const showExportButton = activeTab === 0 || activeTab === 2 || activeTab === 3;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReportsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4">Reports</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {showExportButton && (
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
              Export CSV
            </Button>
          )}
          {activeTab === 0 && (
            <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(_, v) => { if (v) setRange(v); }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: 'text.secondary',
                borderColor: 'divider',
                px: 2,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: (t: any) => `${t.palette.primary.main}26`,
                  color: 'primary.main',
                  borderColor: (t: any) => `${t.palette.primary.main}4D`,
                },
              },
            }}
          >
            <ToggleButton value="week">This Week</ToggleButton>
            <ToggleButton value="month">This Month</ToggleButton>
            <ToggleButton value="lastMonth">Last Month</ToggleButton>
          </ToggleButtonGroup>
        )}
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
        }}
      >
        <Tab label="My Reports" />
        <Tab label="Capacity" icon={<CapacityIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Strategic Drivers" icon={<DriversIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Projects" icon={<ProjectsIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Time Allocation" icon={<TimeAllocIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Activity" icon={<ActivityIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Delegation" icon={<DelegationIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        {canSeeCrossExec && <Tab label="Cross-Executive" icon={<CrossExecIcon sx={{ fontSize: 18 }} />} iconPosition="start" />}
      </Tabs>

      {/* My Reports Tab */}
      {activeTab === 0 && (
        <>
      <HeadlineInsight
        result={deriveExecutiveInsight(
          filteredItems.length === 0
            ? { totalPlanned: 0, done: 0, carried: 0, longCarry: 0 }
            : (() => {
                const done = filteredItems.filter((i) => i.status === 'Done').length;
                const carried = filteredItems.filter((i) => i.isCarryForward).length;
                const fourDaysMs = 4 * 24 * 60 * 60 * 1000;
                const now = Date.now();
                const longCarry = filteredItems.filter(
                  (i) =>
                    i.isCarryForward &&
                    i.originalDate &&
                    now - new Date(i.originalDate).getTime() >= fourDaysMs,
                ).length;
                return {
                  totalPlanned: filteredItems.length,
                  done,
                  carried,
                  longCarry,
                };
              })(),
        )}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && filteredItems.length === 0 && (
        <Card sx={cardSx}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No work items found for this period</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Adjust the date range or create work items to see analytics.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && filteredItems.length > 0 && (
        <Grid container spacing={2.5}>
          {/* Time Allocation Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Time Allocation')}
                {timeAllocation.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={timeAllocation}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {timeAllocation.map((entry, index) => (
                          <Cell key={index} fill={entry.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No time data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Priority Distribution Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Priority Distribution')}
                {priorityDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={priorityDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {priorityDistribution.map((entry, index) => (
                          <Cell key={index} fill={entry.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No priority data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Work Item Breakdown Bar Chart */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Work Item Breakdown')}
                {typeBreakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={typeBreakdown} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                      <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: C.textSecondary, fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                      <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]} barSize={16}>
                        {typeBreakdown.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                      <Bar dataKey="minutes" name="Est. Minutes" radius={[0, 4, 4, 0]} barSize={16} fillOpacity={0.5}>
                        {typeBreakdown.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No type data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Alignment Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Strategic Alignment')}
                {strategicAlignment.length > 0 ? (
                  <Box>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={strategicAlignment}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="value"
                          labelLine={false}
                          label={renderCustomLabel}
                        >
                          {strategicAlignment.map((entry, index) => (
                            <Cell key={index} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {drivers.length > 0 && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {drivers.map(d => (
                          <Box key={d.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: C.textSecondary }}>{d.title}</Typography>
                              <Typography variant="caption" sx={{ color: C.primary, fontWeight: 600 }}>{d.progressPercentage}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={d.progressPercentage} sx={{ height: 3, borderRadius: 2 }} />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Time Hogs Table */}
          <Grid size={{ xs: 12 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Time Hogs')}
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Top 10 most time-consuming work items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Priority</TableCell>
                        <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Est. Minutes</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeHogs.map(item => {
                        const isHigh = item.estimatedMinutes > 90;
                        return (
                          <TableRow
                            key={item.id}
                            sx={{
                              bgcolor: isHigh ? 'rgba(255, 82, 82, 0.06)' : 'transparent',
                              '& td': { borderColor: C.gridLine },
                            }}
                          >
                            <TableCell sx={{ color: C.textPrimary, fontWeight: isHigh ? 600 : 400 }}>
                              {item.title}
                              {isHigh && (
                                <Chip label="90m+" size="small" sx={{ ml: 1, bgcolor: 'rgba(255,82,82,0.15)', color: C.error, fontSize: '0.65rem', height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.type}
                                size="small"
                                sx={{ bgcolor: `${TYPE_COLORS[item.type]}20`, color: TYPE_COLORS[item.type], fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.priority}
                                size="small"
                                sx={{ bgcolor: `${PRIORITY_COLORS[item.priority]}20`, color: PRIORITY_COLORS[item.priority], fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: isHigh ? C.error : C.textPrimary, fontWeight: 600 }}>
                              {item.estimatedMinutes}m
                            </TableCell>
                            <TableCell>
                              <Chip label={item.status} size="small" variant="outlined" sx={{ borderColor: 'divider', color: C.textSecondary }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {timeHogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', color: C.textSecondary, py: 4 }}>
                            No items found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Carry-Forward Frequency */}
          <Grid size={{ xs: 12 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Carry-Forward Frequency')}
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Items that keep getting pushed: potential blockers requiring attention.
                </Typography>
                {carryForwardData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, carryForwardData.length * 40 + 40)}>
                    <BarChart data={carryForwardData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                      <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="title"
                        tick={{ fill: C.textSecondary, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={180}
                        tickFormatter={(value: string) => value.length > 28 ? value.substring(0, 25) + '...' : value}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                      <Bar dataKey="count" name="Times Carried Forward" fill={C.warning} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No carry-forward items in this period</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Capacity Forecast */}
          {capacityForecast && (capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Card sx={cardSx}>
                <CardContent>
                  {sectionTitle('Capacity Forecast')}
                  <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                    Planned vs available minutes for the next 4 weeks
                  </Typography>
                  <Stack spacing={2}>
                    {(capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).map((week: any, i: number) => {
                      const utilisation = week.utilisationPercent ?? (week.availableMinutes > 0 ? Math.round((week.plannedMinutes / week.availableMinutes) * 100) : 0);
                      const isOverloaded = week.isOverloaded ?? utilisation > 100;
                      return (
                        <Box key={week.weekStart || i}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: C.textSecondary }}>
                              {week.weekStart || week.weekLabel} to {week.weekEnd || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isOverloaded ? C.error : C.textPrimary, fontWeight: 600 }}>
                              {utilisation}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, utilisation)}
                            color={isOverloaded ? 'error' : 'primary'}
                            sx={{ height: 8, borderRadius: 4, mb: 0.5, bgcolor: 'rgba(255,255,255,0.06)' }}
                          />
                          <Typography variant="caption" sx={{ color: isOverloaded ? C.error : C.textSecondary }}>
                            {week.plannedMinutes}min planned / {week.availableMinutes}min available
                            {isOverloaded && ', overloaded'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
        </>
      )}

      {/* Capacity Tab */}
      {activeTab === 1 && (
        <CapacityTab capacityForecast={capacityForecast} />
      )}

      {/* Strategic Drivers Tab */}
      {activeTab === 2 && (
        <StrategicDriversTab drivers={drivers} />
      )}

      {/* Projects Tab */}
      {activeTab === 3 && (
        <ProjectsTab drivers={drivers} workItems={items} />
      )}

      {/* Time Allocation Tab */}
      {activeTab === 4 && (
        <TimeAllocationTab />
      )}

      {/* Activity Feed Tab */}
      {activeTab === 5 && (
        <ActivityFeedTab />
      )}

      {/* Delegation Tab */}
      {activeTab === 6 && (
        <DelegationTab />
      )}

      {/* Cross-Executive Tab */}
      {activeTab === 7 && canSeeCrossExec && (
        <CrossExecutiveTab />
      )}
    </Box>
  );
}

// ---- Capacity Forecast Tab ----

function CapacityTab({ capacityForecast }: { capacityForecast: any }) {
  const C = useChartColors();
  const theme = useTheme();

  const weeks: any[] = capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : []);

  const getUtilisation = (week: any): number => {
    if (week.utilisationPercent != null) return week.utilisationPercent;
    if (week.availableMinutes > 0) return Math.round((week.plannedMinutes / week.availableMinutes) * 100);
    return 0;
  };

  const getStatus = (utilisation: number): { label: string; color: string } => {
    if (utilisation > 90) return { label: 'Overloaded', color: theme.palette.error.main };
    if (utilisation >= 70) return { label: 'Busy', color: theme.palette.warning.main };
    return { label: 'Good', color: theme.palette.success.main };
  };

  const avgUtilisation = weeks.length > 0
    ? Math.round(weeks.reduce((sum, w) => sum + getUtilisation(w), 0) / weeks.length)
    : 0;

  const toHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;

  if (!capacityForecast || weeks.length === 0) {
    return (
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CapacityIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">No capacity forecast data available</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Capacity forecast data will appear once you have active plans and calendar events.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const avgStatus = getStatus(avgUtilisation);
  const insight = deriveCapacityInsight(weeks);

  return (
    <Box>
      <HeadlineInsight result={insight} />
      {/* Summary card */}
      <Card sx={{ bgcolor: 'background.paper', mb: 2.5 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${avgStatus.color}15`, border: '2px solid', borderColor: avgStatus.color,
          }}>
            <Typography variant="h6" sx={{ color: avgStatus.color, fontWeight: 700 }}>{avgUtilisation}%</Typography>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: C.textPrimary, fontWeight: 600 }}>
              Average utilisation: {avgUtilisation}% over next {weeks.length} weeks
            </Typography>
            <Typography variant="body2" sx={{ color: C.textSecondary }}>
              Status: <span style={{ color: avgStatus.color, fontWeight: 600 }}>{avgStatus.label}</span>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Forecast table */}
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
            Capacity forecast: next {weeks.length} weeks
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Week Start</TableCell>
                  <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Available Hours</TableCell>
                  <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Meeting Hours</TableCell>
                  <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Planned Work Hours</TableCell>
                  <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Utilisation %</TableCell>
                  <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weeks.map((week: any, i: number) => {
                  const utilisation = getUtilisation(week);
                  const status = getStatus(utilisation);
                  const availableHrs = toHours(week.availableMinutes || 0);
                  const meetingHrs = toHours(week.meetingMinutes || 0);
                  const plannedHrs = toHours(week.plannedMinutes || 0);
                  const weekLabel = week.weekStart || week.weekLabel || `Week ${i + 1}`;
                  return (
                    <TableRow key={weekLabel} sx={{ '& td': { borderColor: C.divider } }}>
                      <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{weekLabel}</TableCell>
                      <TableCell align="right" sx={{ color: C.textPrimary }}>{availableHrs}h</TableCell>
                      <TableCell align="right" sx={{ color: C.textPrimary }}>{meetingHrs}h</TableCell>
                      <TableCell align="right" sx={{ color: C.textPrimary }}>{plannedHrs}h</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, utilisation)}
                            sx={{
                              width: 60, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                              '& .MuiLinearProgress-bar': { bgcolor: status.color, borderRadius: 3 },
                            }}
                          />
                          <Typography variant="body2" sx={{ color: status.color, fontWeight: 600, minWidth: 40 }}>
                            {utilisation}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{ bgcolor: `${status.color}20`, color: status.color, fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

// ---- Strategic Drivers Tab ----

function StrategicDriversTab({ drivers }: { drivers: AnnualStrategicDriver[] }) {
  const C = useChartColors();
  const theme = useTheme();
  const { user } = useAuth();
  const canSeeAlignment = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.role === 'ExecutiveAssistant';
  const [alignmentData, setAlignmentData] = useState<any[]>([]);
  const [alignmentLoading, setAlignmentLoading] = useState(false);

  useEffect(() => {
    if (!canSeeAlignment) return;
    setAlignmentLoading(true);
    driversApi.getAlignment()
      .then((data: any) => setAlignmentData(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setAlignmentLoading(false));
  }, [canSeeAlignment]);

  // Group alignment data by executive (must be before any early returns)
  const alignmentByExec = useMemo(() => {
    const groups: Record<string, { name: string; drivers: any[] }> = {};
    alignmentData.forEach((d: any) => {
      if (!groups[d.executiveUserId]) {
        groups[d.executiveUserId] = { name: d.executiveName, drivers: [] };
      }
      groups[d.executiveUserId].drivers.push(d);
    });
    return Object.values(groups);
  }, [alignmentData]);

  // Detect alignment: drivers with similar titles across executives
  const alignedDriverTitles = useMemo(() => {
    if (alignmentByExec.length < 2) return new Set<string>();
    const titleMap: Record<string, string[]> = {};
    alignmentData.forEach((d: any) => {
      const normalized = d.title.toLowerCase().trim();
      if (!titleMap[normalized]) titleMap[normalized] = [];
      if (!titleMap[normalized].includes(d.executiveUserId)) titleMap[normalized].push(d.executiveUserId);
    });
    const aligned = new Set<string>();
    Object.entries(titleMap).forEach(([title, execs]) => {
      if (execs.length > 1) aligned.add(title);
    });
    return aligned;
  }, [alignmentByExec, alignmentData]);

  if (drivers.length === 0) {
    return (
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <DriversIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">No active strategic drivers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure strategic drivers to see KPI tracking and progress here.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getKpiStatus = (kpi: StructuredKpi): { label: string; color: string } => {
    if (kpi.status === 'Ahead') return { label: 'Ahead', color: theme.palette.success.main };
    if (kpi.status === 'OnTrack') return { label: 'On Track', color: theme.palette.success.main };
    if (kpi.status === 'Behind') return { label: 'Behind', color: theme.palette.error.main };
    if (kpi.status === 'AtRisk') return { label: 'At Risk', color: theme.palette.warning.main };
    return { label: 'Unknown', color: theme.palette.text.secondary };
  };

  const getDirectionArrow = (direction?: string): string => {
    if (direction === 'Up') return '\u2191';
    if (direction === 'Down') return '\u2193';
    if (direction === 'Maintain') return '\u2192';
    return '';
  };

  const getDriverRag = (driver: AnnualStrategicDriver): { color: string; label: string } => {
    const kpis = driver.structuredKpis;
    if (!kpis || kpis.length === 0) {
      // Fall back to progress percentage
      if (driver.progressPercentage >= 60) return { color: theme.palette.success.main, label: 'Green' };
      if (driver.progressPercentage >= 30) return { color: theme.palette.warning.main, label: 'Amber' };
      return { color: theme.palette.error.main, label: 'Red' };
    }
    const behindCount = kpis.filter(k => k.status === 'Behind' || k.status === 'AtRisk').length;
    const aheadOrOnTrack = kpis.filter(k => k.status === 'Ahead' || k.status === 'OnTrack').length;
    if (behindCount > kpis.length / 2) return { color: theme.palette.error.main, label: 'Red' };
    if (behindCount > 0) return { color: theme.palette.warning.main, label: 'Amber' };
    return { color: theme.palette.success.main, label: 'Green' };
  };

  // alignmentByExec and alignedDriverTitles are defined above the early return

  const getKpiHealthColor = (health: string) => {
    if (health === 'Green') return theme.palette.success.main;
    if (health === 'Amber') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      <Stack spacing={2.5}>
        {drivers.map(driver => {
          const rag = getDriverRag(driver);
          const kpis = driver.structuredKpis || [];
          return (
            <Card key={driver.id} sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                {/* Driver header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 12, height: 12, borderRadius: '50%', bgcolor: rag.color, flexShrink: 0,
                  }} />
                  <Typography variant="h6" sx={{ color: C.textPrimary, fontWeight: 600, flex: 1 }}>
                    {driver.title}
                  </Typography>
                  <Chip
                    label={rag.label}
                    size="small"
                    sx={{ bgcolor: `${rag.color}20`, color: rag.color, fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ color: C.primary, fontWeight: 600 }}>
                    {driver.progressPercentage}%
                  </Typography>
                </Box>

                {/* Progress bar */}
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, driver.progressPercentage)}
                  sx={{
                    height: 8, borderRadius: 4, mb: 2, bgcolor: 'rgba(255,255,255,0.06)',
                    '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: rag.color },
                  }}
                />

                {/* KPI Table */}
                {kpis.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>KPI</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Current</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Target</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Direction</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Range</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {kpis.map((kpi, i) => {
                          const kpiStatus = getKpiStatus(kpi);
                          const unit = kpi.unit || '';
                          const hasBaseline = kpi.baseline != null;
                          const hasCurrent = kpi.currentValue != null;
                          const hasTarget = kpi.targetValue != null;
                          return (
                            <TableRow key={i} sx={{ '& td': { borderColor: C.divider } }}>
                              <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>
                                {kpi.name}
                              </TableCell>
                              <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>
                                {hasCurrent ? `${kpi.currentValue}${unit ? ` ${unit}` : ''}` : '--'}
                              </TableCell>
                              <TableCell align="right" sx={{ color: C.textSecondary }}>
                                {hasTarget ? `${kpi.targetValue}${unit ? ` ${unit}` : ''}` : '--'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600 }}>
                                  {getDirectionArrow(kpi.direction)} {kpi.direction || ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={kpiStatus.label}
                                  size="small"
                                  sx={{ bgcolor: `${kpiStatus.color}20`, color: kpiStatus.color, fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell>
                                {hasBaseline && hasTarget ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: C.textSecondary }}>{kpi.baseline}</Typography>
                                    <Box sx={{ flex: 1, position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)', minWidth: 60 }}>
                                      {hasCurrent && hasTarget && kpi.baseline != null && (
                                        <Box sx={{
                                          position: 'absolute', top: 0, left: 0, height: '100%', borderRadius: 3,
                                          bgcolor: kpiStatus.color,
                                          width: `${Math.min(100, Math.max(0, ((kpi.currentValue! - kpi.baseline) / (kpi.targetValue! - kpi.baseline)) * 100))}%`,
                                        }} />
                                      )}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: C.textSecondary }}>{kpi.targetValue}</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" sx={{ color: C.textSecondary }}>--</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : driver.kpis.length > 0 ? (
                  /* Fallback: show plain string KPIs */
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: C.textSecondary, mb: 1 }}>KPIs</Typography>
                    <Stack spacing={0.5}>
                      {driver.kpis.map((kpi, i) => (
                        <Typography key={i} variant="body2" sx={{ color: C.textPrimary }}>
                          {'\u2022'} {kpi}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: C.textSecondary }}>
                    No KPIs configured for this driver.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Strategic Alignment View (Admin/EA only) */}
      {canSeeAlignment && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
            Strategic Alignment (Cross-Executive)
          </Typography>
          {alignmentLoading && <LinearProgress sx={{ mb: 2 }} />}
          {!alignmentLoading && alignmentByExec.length === 0 && (
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No cross-executive driver data available. This view requires multiple executives in your tenant.
                </Typography>
              </CardContent>
            </Card>
          )}
          {!alignmentLoading && alignmentByExec.length > 0 && (
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Executive</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Driver</TableCell>
                        <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Progress</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>KPI Health</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alignmentData.map((row: any) => {
                        const isAligned = alignedDriverTitles.has(row.title.toLowerCase().trim());
                        return (
                          <TableRow key={row.id} sx={{
                            '& td': { borderColor: C.divider },
                            bgcolor: isAligned ? 'rgba(108, 155, 255, 0.06)' : 'transparent',
                          }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>
                              {row.executiveName}
                            </TableCell>
                            <TableCell sx={{ color: C.textPrimary }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {row.title}
                                {isAligned && (
                                  <Chip label="Aligned" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, row.progressPercentage)}
                                  sx={{
                                    width: 60, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 3 },
                                  }}
                                />
                                <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, minWidth: 36 }}>
                                  {Math.round(row.progressPercentage)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.kpiHealth}
                                size="small"
                                sx={{
                                  bgcolor: `${getKpiHealthColor(row.kpiHealth)}20`,
                                  color: getKpiHealthColor(row.kpiHealth),
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.lifecycleStatus || 'Active'}
                                size="small"
                                variant="outlined"
                                color={row.lifecycleStatus === 'Active' ? 'success' : row.lifecycleStatus === 'Review' ? 'warning' : 'default'}
                                sx={{ height: 22 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {alignedDriverTitles.size > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Highlighted rows indicate drivers with matching titles across multiple executives.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}

// ---- Projects Report Tab ----

function ProjectsTab({ drivers, workItems }: { drivers: AnnualStrategicDriver[]; workItems: WorkItem[] }) {
  const C = useChartColors();
  const theme = useTheme();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await projectsApi.getPortfolio();
        setProjects(data);
      } catch (err) {
        console.error('Failed to load projects', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // RAG counts
  const ragCounts = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    return {
      Green: active.filter(p => p.ragStatus === 'Green').length,
      Amber: active.filter(p => p.ragStatus === 'Amber').length,
      Red: active.filter(p => p.ragStatus === 'Red').length,
    };
  }, [projects]);

  // Time allocation by project (from work items linked to projects)
  const timeByProject = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const projectTimes = active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const totalMinutes = linkedItems.reduce((sum, wi) => sum + wi.estimatedMinutes, 0);
      return { id: project.id, name: project.title, totalMinutes, totalHours: +(totalMinutes / 60).toFixed(1) };
    }).filter(p => p.totalMinutes > 0).sort((a, b) => b.totalMinutes - a.totalMinutes);

    const grandTotal = projectTimes.reduce((s, p) => s + p.totalMinutes, 0);
    return projectTimes.map(p => ({
      ...p,
      percentage: grandTotal > 0 ? Math.round((p.totalMinutes / grandTotal) * 100) : 0,
    }));
  }, [projects, workItems]);

  // Strategic alignment: drivers with/without projects
  const alignment = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const driverIdsWithProjects = new Set(active.map(p => p.strategicDriverId).filter(Boolean));
    const withProjects = drivers.filter(d => driverIdsWithProjects.has(d.id));
    const withoutProjects = drivers.filter(d => !driverIdsWithProjects.has(d.id));
    return { withProjects, withoutProjects };
  }, [projects, drivers]);

  // Stale projects: no work item updates and no indicator updates in last 14 days
  const staleProjects = useMemo(() => {
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');

    return active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const latestWorkItemUpdate = linkedItems.length > 0
        ? new Date(Math.max(...linkedItems.map(wi => new Date(wi.updatedAt).getTime())))
        : null;
      const projectUpdate = new Date(project.updatedAt);
      const lastActivity = latestWorkItemUpdate && latestWorkItemUpdate > projectUpdate
        ? latestWorkItemUpdate
        : projectUpdate;
      const daysSinceActivity = Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
      return { ...project, lastActivity, daysSinceActivity };
    }).filter(p => p.daysSinceActivity >= 14).sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);
  }, [projects, workItems]);

  const ragColor = (rag: string) => {
    if (rag === 'Green') return theme.palette.success.main;
    if (rag === 'Amber') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && projects.length === 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ProjectsIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No projects found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create projects to see portfolio reporting here.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && projects.length > 0 && (
        <Grid container spacing={2.5}>
          {/* Portfolio RAG Summary */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Portfolio RAG Summary</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>{ragCounts.Green}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Green</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.warning.main, fontWeight: 700 }}>{ragCounts.Amber}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Amber</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.error.main, fontWeight: 700 }}>{ragCounts.Red}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Red</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Time Allocation by Project */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Time Allocation by Project</Typography>
                {timeByProject.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Project</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Hours</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600, width: '40%' }}>Allocation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeByProject.map(p => (
                          <TableRow key={p.id} sx={{ '& td': { borderColor: C.gridLine } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{p.name}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{p.totalHours}h</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={p.percentage}
                                  sx={{
                                    flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4 },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: C.textSecondary, minWidth: 32, fontWeight: 600 }}>
                                  {p.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No time tracked against projects yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Alignment */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Strategic Alignment</Typography>
                {drivers.length > 0 ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ color: C.success, fontWeight: 600, mb: 1 }}>
                        Drivers with Projects ({alignment.withProjects.length})
                      </Typography>
                      {alignment.withProjects.length > 0 ? (
                        <Stack spacing={1}>
                          {alignment.withProjects.map(d => (
                            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.success, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: C.textPrimary }}>{d.title}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: C.textSecondary }}>None</Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ color: C.warning, fontWeight: 600, mb: 1 }}>
                        Drivers without Projects ({alignment.withoutProjects.length})
                      </Typography>
                      {alignment.withoutProjects.length > 0 ? (
                        <Stack spacing={1}>
                          {alignment.withoutProjects.map(d => (
                            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.warning, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: C.textPrimary }}>{d.title}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: C.textSecondary }}>None</Typography>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No strategic drivers configured
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Stale Project Detection */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, color: C.textPrimary, fontWeight: 600 }}>Stale Project Detection</Typography>
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Projects with no activity in the last 14 days
                </Typography>
                {staleProjects.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Project</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>RAG</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Last Activity</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Days Stale</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staleProjects.map(p => (
                          <TableRow key={p.id} sx={{ '& td': { borderColor: C.gridLine } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{p.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={p.ragStatus}
                                size="small"
                                sx={{ bgcolor: `${ragColor(p.ragStatus)}20`, color: ragColor(p.ragStatus), fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: C.textSecondary }}>
                              {p.lastActivity.toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ color: p.daysSinceActivity >= 30 ? C.error : C.warning, fontWeight: 600 }}
                              >
                                {p.daysSinceActivity}d
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">All projects have recent activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ---- Time Allocation Tab ----

function TimeAllocationTab() {
  const C = useChartColors();
  const theme = useTheme();
  const [report, setReport] = useState<TimeAllocationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { start, end } = getDateRange(dateRange);
        const from = start.toISOString().split('T')[0];
        const to = end.toISOString().split('T')[0];
        const data = await reportingApi.getTimeAllocation(from, to);
        setReport(data);
      } catch (err) {
        console.error('Failed to load time allocation', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateRange]);

  const toHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;

  const meetingVsFocus = useMemo(() => {
    if (!report) return [];
    return [
      { name: 'Meetings', value: report.totalMeetingMinutes, color: theme.palette.secondary.main },
      { name: 'Focus Time', value: report.totalFocusMinutes, color: theme.palette.primary.main },
    ].filter(d => d.value > 0);
  }, [report, theme]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: C.textPrimary, fontWeight: 600 }}>
          Time Allocation Analytics
        </Typography>
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, v) => { if (v) setDateRange(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'divider',
              px: 2,
              py: 0.75,
              fontSize: '0.8rem',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: (t: any) => `${t.palette.primary.main}26`,
                color: 'primary.main',
                borderColor: (t: any) => `${t.palette.primary.main}4D`,
              },
            },
          }}
        >
          <ToggleButton value="week">This Week</ToggleButton>
          <ToggleButton value="month">This Month</ToggleButton>
          <ToggleButton value="lastMonth">Last Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <HeadlineInsight result={deriveTimeAllocationInsight(report)} />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && report && (
        <Grid container spacing={2.5}>
          {/* Meeting vs Focus Time */}
          {meetingVsFocus.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
                    Meeting vs Focus Time
                  </Typography>
                  <Stack spacing={2}>
                    {meetingVsFocus.map(item => (
                      <Box key={item.name}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 500 }}>{item.name}</Typography>
                          <Typography variant="body2" sx={{ color: item.color, fontWeight: 600 }}>{toHours(item.value)}h</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={report.totalMinutes > 0 ? Math.min(100, (item.value / report.totalMinutes) * 100) : 0}
                          sx={{
                            height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.06)',
                            '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: item.color },
                          }}
                        />
                      </Box>
                    ))}
                    <Typography variant="caption" sx={{ color: C.textSecondary }}>
                      Total tracked: {toHours(report.totalMinutes)}h
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* By Strategic Driver */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
                  By Strategic Driver
                </Typography>
                {report.byDriver.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Driver</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Hours</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600, width: '35%' }}>Share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.byDriver.map(d => (
                          <TableRow key={d.driverId} sx={{ '& td': { borderColor: C.divider } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{d.driverName}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{toHours(d.minutes)}h</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={d.percentage}
                                  sx={{
                                    flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4 },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: C.textSecondary, minWidth: 32, fontWeight: 600 }}>
                                  {d.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No completed work linked to strategic drivers in this period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* By Project */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
                  By Project
                </Typography>
                {report.byProject.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Project</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Hours</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600, width: '35%' }}>Share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.byProject.map(p => (
                          <TableRow key={p.projectId} sx={{ '& td': { borderColor: C.divider } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{p.projectName}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{toHours(p.minutes)}h</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={p.percentage}
                                  color="secondary"
                                  sx={{
                                    flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4 },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: C.textSecondary, minWidth: 32, fontWeight: 600 }}>
                                  {p.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No completed work linked to projects in this period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* By Work Type */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
                  By Work Type
                </Typography>
                {report.byType.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Type</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600 }}>Hours</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.divider, fontWeight: 600, width: '30%' }}>Share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.byType.map(t => (
                          <TableRow key={t.type} sx={{ '& td': { borderColor: C.divider } }}>
                            <TableCell>
                              <Chip
                                label={t.type}
                                size="small"
                                sx={{ bgcolor: `${TYPE_COLORS[t.type] || C.primary}20`, color: TYPE_COLORS[t.type] || C.primary, fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{t.count}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{toHours(t.minutes)}h</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={t.percentage}
                                  sx={{
                                    flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: TYPE_COLORS[t.type] || C.primary },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: C.textSecondary, minWidth: 32, fontWeight: 600 }}>
                                  {t.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No completed work items in this period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {!loading && !report && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <TimeAllocIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No time allocation data available</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Complete work items to see time allocation analytics here.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ---- Activity Feed Tab ----

function getActivityConfig(type: string, theme: any): { icon: React.ReactNode; color: string; label: string } {
  switch (type) {
    case 'WorkItemCompleted':
      return { icon: <CheckCircleOutline sx={{ fontSize: 20 }} />, color: theme.palette.success.main, label: 'Completed' };
    case 'WorkItemCreated':
      return { icon: <AddCircleOutline sx={{ fontSize: 20 }} />, color: theme.palette.primary.main, label: 'Created' };
    case 'PlanCreated':
      return { icon: <EventNote sx={{ fontSize: 20 }} />, color: theme.palette.info.main, label: 'Plan' };
    case 'KpiRecorded':
      return { icon: <ShowChart sx={{ fontSize: 20 }} />, color: theme.palette.warning.main, label: 'KPI' };
    case 'ReflectionAdded':
      return { icon: <EditIcon sx={{ fontSize: 20 }} />, color: theme.palette.secondary.main, label: 'Reflection' };
    case 'ProjectUpdated':
      return { icon: <FolderSpecial sx={{ fontSize: 20 }} />, color: theme.palette.info.dark, label: 'Project' };
    default:
      return { icon: <ActivityIcon sx={{ fontSize: 20 }} />, color: theme.palette.text.secondary, label: type };
  }
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function ActivityFeedTab() {
  const C = useChartColors();
  const theme = useTheme();
  const [feed, setFeed] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await reportingApi.getActivityFeed(50);
        setFeed(data);
      } catch (err) {
        console.error('Failed to load activity feed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: C.textPrimary, fontWeight: 600 }}>
        Recent Activity
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && feed.length === 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ActivityIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No recent activity</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Activity will appear here as you create work items, record KPIs, and complete plans.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && feed.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 0 }}>
            <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              {feed.map((item, i) => {
                const config = getActivityConfig(item.type, theme);
                return (
                  <Box
                    key={`${item.entityId}-${item.type}-${i}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 2.5,
                      py: 1.5,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                    }}
                  >
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${config.color}15`, color: config.color, flexShrink: 0,
                    }}>
                      {config.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.textSecondary }}>
                        {item.entityType}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{ bgcolor: `${config.color}20`, color: config.color, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" sx={{ color: C.textSecondary, whiteSpace: 'nowrap', minWidth: 55, textAlign: 'right' }}>
                        {formatRelativeTime(item.timestamp)}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ---- Delegation Effectiveness Tab ----

function DelegationTab() {
  const C = useChartColors();
  const [report, setReport] = useState<DelegationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { start, end } = getDateRange(range);
        const from = start.toISOString().slice(0, 10);
        const to = end.toISOString().slice(0, 10);
        const data = await reportingApi.getDelegationReport(from, to);
        setReport(data);
      } catch (err) {
        console.error('Failed to load delegation report', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [range]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, v) => { if (v) setRange(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'divider',
              px: 2,
              py: 0.75,
              fontSize: '0.8rem',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: (t: any) => `${t.palette.primary.main}26`,
                color: 'primary.main',
                borderColor: (t: any) => `${t.palette.primary.main}4D`,
              },
            },
          }}
        >
          <ToggleButton value="week">This Week</ToggleButton>
          <ToggleButton value="month">This Month</ToggleButton>
          <ToggleButton value="lastMonth">Last Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <HeadlineInsight result={deriveDelegationInsight(report)} />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && report && (
        <Grid container spacing={2.5}>
          {/* Summary Cards */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {report.totalDelegated}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Total Delegated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 700 }}>
                  {report.completionRate}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Completion Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: C.secondary, fontWeight: 700 }}>
                  {report.completed}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: C.warning, fontWeight: 700 }}>
                  {report.averageTurnaroundDays}d
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Avg Turnaround
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* By Delegatee Table */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>By Delegatee</Typography>
                {report.byDelegatee.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, fontWeight: 600, borderColor: C.divider }}>Name</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, fontWeight: 600, borderColor: C.divider }}>Received</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, fontWeight: 600, borderColor: C.divider }}>Completed</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, fontWeight: 600, borderColor: C.divider }}>Completion Rate</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, fontWeight: 600, borderColor: C.divider }}>Avg Days</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.byDelegatee.map(d => (
                          <TableRow key={d.userId}>
                            <TableCell sx={{ color: C.textPrimary, borderColor: C.divider }}>{d.name}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, borderColor: C.divider }}>{d.received}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, borderColor: C.divider }}>{d.completed}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, borderColor: C.divider }}>
                              {d.received > 0 ? Math.round((d.completed / d.received) * 100) : 0}%
                            </TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, borderColor: C.divider }}>{d.avgDays}d</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No delegated items found for this period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {!loading && !report && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <DelegationIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No delegation data available</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Delegate work items to see effectiveness metrics here.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ---- Cross-Executive Report Tab ----

function CrossExecutiveTab() {
  const C = useChartColors();
  const [crossData, setCrossData] = useState<any>(null);
  const [forecast, setForecast] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { start, end } = getDateRange(dateRange);
        const from = start.toISOString().split('T')[0];
        const to = end.toISOString().split('T')[0];
        const [crossResult, forecastResult] = await Promise.allSettled([
          reportingApi.getCrossExecutiveReport(from, to),
          reportingApi.getCapacityForecast(4),
        ]);
        if (crossResult.status === 'fulfilled') setCrossData(crossResult.value);
        if (forecastResult.status === 'fulfilled') setForecast(forecastResult.value);
        if (crossResult.status === 'rejected' && forecastResult.status === 'rejected') {
          setError('Failed to load cross-executive reports.');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateRange]);

  const executives: any[] = crossData?.executives || crossData?.reports || (Array.isArray(crossData) ? crossData : []);
  const forecastWeeks: any[] = forecast?.weeks || (Array.isArray(forecast) ? forecast : []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: C.textPrimary, fontWeight: 600 }}>
          Cross-Executive Overview
        </Typography>
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, v) => { if (v) setDateRange(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'divider',
              px: 2,
              py: 0.75,
              fontSize: '0.8rem',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: (t: any) => `${t.palette.primary.main}26`,
                color: 'primary.main',
                borderColor: (t: any) => `${t.palette.primary.main}4D`,
              },
            },
          }}
        >
          <ToggleButton value="week">This Week</ToggleButton>
          <ToggleButton value="month">This Month</ToggleButton>
          <ToggleButton value="lastMonth">Last Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Executive Comparison Table */}
      {!loading && executives.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2.5 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
              Executive Comparison
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Executive</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Completion Rate</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Focus Time Ratio</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Carry-Forward</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Strategic Alignment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executives.map((exec: any, i: number) => {
                    const completionRate = exec.completionRate ?? exec.completionPercent ?? 0;
                    const focusRatio = exec.focusTimeRatio ?? exec.focusTimePercent ?? 0;
                    const carryForward = exec.carryForwardCount ?? exec.carryForwards ?? 0;
                    const strategicPct = exec.strategicAlignmentPercent ?? exec.strategicAlignment ?? 0;
                    return (
                      <TableRow key={exec.executiveUserId || exec.executiveName || i} sx={{ '& td': { borderColor: C.gridLine } }}>
                        <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>
                          {exec.executiveName || exec.displayName || exec.email || `Executive ${i + 1}`}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(completionRate, 100)}
                              sx={{
                                width: 60, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                                '& .MuiLinearProgress-bar': { bgcolor: completionRate >= 70 ? C.success : completionRate >= 40 ? C.warning : C.error, borderRadius: 3 },
                              }}
                            />
                            <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, minWidth: 40 }}>
                              {Math.round(completionRate)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: focusRatio >= 30 ? C.success : C.warning, fontWeight: 600 }}>
                            {Math.round(focusRatio)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={carryForward}
                            size="small"
                            sx={{
                              bgcolor: carryForward > 5 ? 'rgba(255,82,82,0.15)' : 'rgba(255,255,255,0.06)',
                              color: carryForward > 5 ? C.error : C.textPrimary,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: strategicPct >= 50 ? C.primary : C.textSecondary, fontWeight: 600 }}>
                            {Math.round(strategicPct)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!loading && executives.length === 0 && !error && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2.5 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CrossExecIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No cross-executive data available</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cross-executive reports require multiple executives with active plans.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Capacity Forecast */}
      {!loading && forecastWeeks.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
              Capacity forecast: next 4 weeks
            </Typography>
            <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
              Planned minutes vs available minutes per week
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={forecastWeeks} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} />
                <XAxis
                  dataKey="weekLabel"
                  tick={{ fill: C.textSecondary, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: string) => value || ''}
                />
                <YAxis
                  tick={{ fill: C.textSecondary, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `${Math.round(value / 60)}h`}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <Box sx={{ backgroundColor: C.tooltipBg, border: '1px solid', borderColor: C.divider, borderRadius: 2, color: C.textPrimary, fontSize: 13, p: '8px 12px' }}>
                        <Typography variant="caption" sx={{ color: C.textSecondary, display: 'block', mb: 0.5 }}>{label}</Typography>
                        {payload.map((entry: any, i: number) => (
                          <Typography key={i} variant="body2" sx={{ color: entry.color }}>
                            {entry.name}: {Math.round(entry.value / 60)}h ({entry.value}m)
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                />
                <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                <Bar dataKey="availableMinutes" name="Available" fill={C.primary} radius={[4, 4, 0, 0]} barSize={32} fillOpacity={0.4} />
                <Bar dataKey="plannedMinutes" name="Planned" fill={C.secondary} radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
